// import { GeoJsonLayer } from '@deck.gl/layers'
import DeckMap from './DeckMap'
// import mapsApiClient from './mapsApiClient'
// import { Matrix4, Vector3 } from '@math.gl/core'
import { Vector3 } from '@math.gl/core'
// import { MVTLayer } from '@deck.gl/geo-layers'
// import * as turf from '@turf/turf'
import { ASSETS, CLUSTER_ASSETS, EXCLUDED_ASSETS } from './constant'
import { FlyToInterpolator } from '@deck.gl/core'
import { waitMS } from '@/utils/delay'
import { isGoogleMapsBasemap } from '@/helpers/google-maps.helper'
import { isArrayEmpty } from '@/helpers/array.helper'
import type {
  LayerService,
  DeckLayerLike,
  BoundingBox,
  GoogleMapsOverlayWithMap,
} from './layerService.types'
import type { Layer } from '@deck.gl/core'
import { Basemap } from '@workspaces/types'
import type { AssetDataGeoJsonLayer } from '@/plan/filter/asset.type'

// When flying to an asset extent, deck.gl's `fitBounds` will zoom all the way
// to the viewport's max zoom for a tight (few close assets) bounding box. At
// that zoom the asset-id label grid can no longer group collided labels, so
// they render on top of each other. Cap the fit zoom to a readable level and
// leave a margin around the extent (mirrors Google Maps' fitBounds behavior).
const FLY_TO_MAX_ZOOM = 17
const FLY_TO_PADDING_PX = 80

// const WORLD_SIZE = 512

// const DEFAULT_MVT_PROPS = {
//   pointRadiusMinPixels: 2,
//   lineWidthMinPixels: 1,
//   getFillColor: [130, 109, 186, 255],
//   getLineColor: [44, 44, 44],
// }

const layerService: LayerService = {
  layers: {},
  deckInstance: null,
  zoom: 0,

  getAppBasemap() {
    if (!this.deckInstance) {
      return 'basemapNotSet'
    }
    return this.deckInstance.getAppBasemap()
  },
  getBaseMap() {
    return (this.deckInstance as DeckMap).getBaseMap()
  },
  isAppBasemapGoogleMaps() {
    return isGoogleMapsBasemap(this.getAppBasemap() as Basemap.Basemap)
  },
  setZoom(zoom) {
    this.zoom = zoom
  },
  getZoom() {
    return this.zoom
  },
  googleMapsZoomIn() {
    const baseMap = this.getBaseMap() as unknown as GoogleMapsOverlayWithMap
    const map = baseMap._map
    const currentZoom = map.getZoom() ?? 0
    map.setZoom(currentZoom + 1)
  },
  googleMapsZoomOut() {
    const baseMap = this.getBaseMap() as unknown as GoogleMapsOverlayWithMap
    const map = baseMap._map
    const currentZoom = map.getZoom() ?? 0
    map.setZoom(currentZoom - 1)
  },
  init(deckProps) {
    this.deckInstance = new DeckMap(deckProps)
  },
  async setGoogleMapsBaseMap(style, viewState) {
    const viewStateParams: Record<string, unknown> = { ...viewState }
    if (viewStateParams.provider === 'OpenStreetMaps') {
      viewStateParams.zoom = (viewStateParams.zoom as number) + 1
    }
    console.debug(
      `🔰 LayerService: setGoogleMapsBaseMap: ${style}, zoom: ${viewStateParams.zoom}`,
    )
    const layers = Object.values(this.layers).map(
      ({ layerType: LayerClass, ...props }) => new LayerClass(props),
    ) as Layer[]
    await (this.deckInstance as DeckMap).setGoogleMapsBaseMap(
      layers,
      style,
      viewStateParams,
    )
  },
  destroy() {
    const deck = this.deckInstance as DeckMap
    deck.finalize()
    // Preserves original `delete` semantics; `deckInstance` is required on the type.
    delete (this as { deckInstance?: unknown }).deckInstance
    this.layers = {}
  },
  hasLayer(id) {
    return !!this.layers[id]
  },
  isVisible(id) {
    // visible = undefined is treated by deckgl as visible = true
    return this.layers[id] && this.layers[id].visible !== false
  },
  getLayerProp(id, prop) {
    return this.layers[id] && this.layers[id][prop]
  },
  isAlreadyInitialized() {
    return !!this.deckInstance
  },
  /*
  async fetchMVTTemplate (sql, options) {
    const data = await mapsApiClient.createMap(sql, options)
    const stats = data.metadata.layers[0].meta.stats
    const urlData = data.metadata.url.vector
    let urls = [urlData.urlTemplate]
    if (urlData.subdomains.length > 0) {
      urls = urlData.subdomains.map(s => urlData.urlTemplate.replace('{s}', s))
    }
    return { urls, stats }
  },

  async addSQLLayer({
    id,
    sql,
    sublayer: SubLayer = GeoJsonLayer,
    requestOptions = {},
    ...props
  }) {
    if (!sql) {
      throw new Error(
        `[layerService.addSQLLayer] sql must be defined. Received ${sql} instead`,
      )
    }
    const { urls, stats } = await this.fetchMVTTemplate(sql, requestOptions)
    // Hack to prevent custom MVT cache
    // TODO; Remove: find a better way to do this, the backend must disable the mvt cache if is possible
    const timestamp = new Date().getTime()
    const urlsWithTimestamp = urls.map((url) => `${url}&timestamp=${timestamp}`)
    this.addLayer({
      id,
      layerType: MVTLayer,
      data: urlsWithTimestamp,
      stats,
      requestOptions,
      ...DEFAULT_MVT_PROPS,
      ...props,
      refinementStrategy: 'no-overlap',
      renderSubLayers:
        props.renderSubLayers ||
        ((innerprops) =>
          new SubLayer({
            ...innerprops,
            extensions: props.disableClipping ? [] : innerprops.extensions,
          })),
    })
  },
  async updateSQLLayer({ id, sql }) {
    if (!this.layers[id]) {
      return
    }
    const requestOptions = this.layers[id].requestOptions
    const { urls, stats } = await this.fetchMVTTemplate(sql, requestOptions)
    this.updateLayer(id, {
      data: urls,
      stats,
    })
  },
  */
  updateDeckInstance(newProps = {}) {
    if (!this.deckInstance) {
      return
    }
    const layers = Object.values(this.layers)
      .sort((l1, l2) => (l1.zIndex || 0) - (l2.zIndex || 0))
      // NOTE: uncomment next line if you are rendering many (>= 5) MVTLayers at the same time
      // .filter(l => this.isVisible(l.id))
      .map(
        ({ layerType: LayerClass, ...props }) => new LayerClass(props),
      ) as Layer[]

    if (this.isAppBasemapGoogleMaps()) {
      // Google Maps path: base map is the GoogleMapsOverlay, which has setProps.
      const overlay = this.getBaseMap() as unknown as GoogleMapsOverlayWithMap
      overlay.setProps({ layers, ...newProps })
    } else {
      this.deckInstance.setProps({ layers, ...newProps })
    }
  },
  // Force a single reconcile+draw after a layer update made while the map is
  // idle. deck.gl 9's AnimationLoop here does not draw a frame just because
  // setProps flagged a redraw, so an idle update (e.g. excluding assets — layer
  // data changes but the camera does not) is staged but never rendered, leaving
  // stale icons on screen. Only call this when NOT running a camera transition:
  // a forced frame advances the transition clock. No-op on the Google Maps path
  // (the overlay renders via Google Maps). See sc-558025 (deck.gl 9 migration).
  forceIdleRedraw() {
    if (!this.deckInstance || this.isAppBasemapGoogleMaps()) {
      return
    }
    const deck = this.deckInstance as DeckMap
    deck.scheduleForcedRedraw()
  },
  addLayer(layer, updateDeckInstance = true) {
    if (!layer.id) {
      throw new Error(
        `[layerService.addLayer] layer id must defined. Received "${layer.id}" instead`,
      )
    }
    if (typeof layer.layerType !== 'function') {
      throw new Error(
        `[layerService.addLayer] layerType must be a function. Received "${layer.layerType}" instead`,
      )
    }
    this.layers[layer.id] = layer
    if (updateDeckInstance) this.updateDeckInstance()
  },
  updateLayer(id, data, updateDeckInstance = true) {
    if (!this.layers[id]) {
      console.warn(
        'WARNING: Trying to update layer that it does not exist: ',
        id,
      )
      return
    }

    this.layers[id] = {
      ...this.layers[id],
      ...data,
    }
    if (updateDeckInstance) {
      this.updateDeckInstance()
    }
  },
  removeLayer(id, updateDeckInstance = true) {
    if (this.hasLayer(id)) {
      delete this.layers[id]
      if (updateDeckInstance) {
        this.updateDeckInstance()
      }
    } else {
      console.warn(`Layer ${id} not found when trying to remove it`)
    }
  },
  hideLayer(id, opts = {}, updateDeckInstance = true) {
    if (this.hasLayer(id)) {
      // Legacy call sites may pass a boolean in the `opts` position; ignore it.
      const optsObj = typeof opts === 'object' ? opts : {}
      this.layers[id].visible = false
      this.updateLayer(id, { ...optsObj, visible: false }, updateDeckInstance)
    } else {
      console.warn(`Layer ${id} not found when trying to hide it`)
    }
  },
  showLayer(id, opts = {}, updateDeckInstance = true) {
    if (this.hasLayer(id)) {
      // Legacy call sites may pass a boolean in the `opts` position; ignore it.
      const optsObj = typeof opts === 'object' ? opts : {}
      this.layers[id].visible = true
      this.updateLayer(id, { visible: true, ...optsObj }, updateDeckInstance)
    } else {
      console.warn(`Layer ${id} not found when trying to show it`)
    }
  },
  getZoomFactor() {
    const viewport = (this.deckInstance as DeckMap).getViewports()[0]
    return (viewport as unknown as { metersPerPixel: number }).metersPerPixel
  },
  getViewport() {
    return (this.deckInstance as DeckMap).getViewports()[0]
  },
  /*
  getFrustumPlanes() {
    const viewports = this.deckInstance.getViewports(undefined)
    return viewports[0].getFrustumPlanes()
  },
  */
  /*
  getBounds() {
    const viewport = this.deckInstance.getViewports()[0]
    return viewport.getBounds()
  },
  */
  /*
  getTransformationMatrixFromTile({ x, y, z }) {
    const worldScale = 2 ** z

    const xScale = WORLD_SIZE / worldScale
    const yScale = -xScale

    const xOffset = (WORLD_SIZE * x) / worldScale
    const yOffset = WORLD_SIZE * (1 - y / worldScale)

    return new Matrix4()
      .translate([xOffset, yOffset, 0])
      .scale([xScale, yScale, 1])
  },
  */
  /*
  transformPoint(point, matrix) {
    return matrix.transform(point)
  },
  */
  isPointInViewport(point, planes) {
    const position = new Vector3(point[0], point[1], 0)
    let outDir = null
    for (const direction in planes) {
      const plane = planes[direction]
      if (position.dot(plane.normal) > plane.distance) {
        outDir = direction
        break
      }
    }

    return outDir === null
  },
  /*
  isFeatureInViewport(feature, cache, matrix, planes) {
    if (!feature.properties) {
      return false
    }
    // TODO: make this configurable
    const id = feature.properties.cartodb_id || feature.id
    if (cache.has(id)) {
      return false // avoid duplicate features across tiles
    }

    let isInside = false
    const type = feature.geometry.type
    if (type === 'Point') {
      const point = this.transformPoint(feature.geometry.coordinates, matrix)
      isInside = this.isPointInViewport(point, planes)
    } else if (type === 'Polygon') {
      const rings = feature.geometry.coordinates
      isInside = rings.some((polygon) =>
        polygon.some((point) =>
          this.isPointInViewport(this.transformPoint(point, matrix), planes),
        ),
      )
    } else if (type === 'MultiPolygon') {
      const polygons = feature.geometry.coordinates
      isInside = polygons.some((ring) =>
        ring.some((polygon) =>
          polygon.some((point) =>
            this.isPointInViewport(this.transformPoint(point, matrix), planes),
          ),
        ),
      )
    } else {
      // eslint-disable-next-line
      console.error(
        '[layerService.js] Unsupported geometry type for viewportFeatues',
        feature.geometry.type,
      )
    }

    if (isInside) {
      cache.add(id)
    }

    return isInside
  },
  */
  getLayer(layerId) {
    if (!this.deckInstance) {
      // Return an empty array as a truthy-but-not-a-layer sentinel so that
      // callers doing `if (!getLayer(id))` don't short-circuit when the deck
      // instance is simply not yet created.  The public type says `undefined`
      // for simplicity; the array cast preserves the original runtime contract.
      return undefined
    }

    // Accessing deck.gl/Google overlay internals (`_map`, `_deck`, `layerManager`)
    // that are not part of the public typed surface.
    const deck = this.deckInstance as unknown as {
      _map: { _deck?: { layerManager: { layers: DeckLayerLike[] } } }
      layerManager: { layers: DeckLayerLike[] }
    }
    let layers: DeckLayerLike[]
    if (this.isAppBasemapGoogleMaps()) {
      if (deck._map._deck) {
        layers = deck._map._deck.layerManager.layers
      } else {
        console.warn('Deck instance is not yet initialized')
        return undefined
      }
    } else {
      layers = deck.layerManager.layers
    }

    const layer = layers.find((l) => l.id === layerId)

    return layer
  },
  /*
  // code adapted from here: https://github.com/CartoDB/toolkit/blob/jb/viewport-features-generator/packages/viz/src/lib/interactivity/viewport-features/ViewportFeaturesGenerator.ts
  getViewportFeatures(layerId) {
    const layer = this.getLayer(layerId)
    if (!layer) {
      return []
    }

    const cache = new Set()
    const planes = this.getFrustumPlanes()
    const tiles = layer.state.tileset.selectedTiles

    return tiles
      .map((t) => {
        const matrix = this.getTransformationMatrixFromTile(t)
        const features = t.content || []
        return features.filter((f) =>
          this.isFeatureInViewport(f, cache, matrix, planes),
        )
      })
      .flat()
  },
  getViewportFeaturesGeoJSON(layerId) {
    const layer = this.getLayer(layerId)
    if (!layer) {
      return []
    }

    const bbox = turf.bboxPolygon(this.getBounds())
    return layer.props.data.filter((f) =>
      turf.booleanPointInPolygon(turf.point([f.lng, f.lat]), bbox),
    )
  },
  */
  getFilteredDataFromAssetLayer() {
    const clusterLayer = this.getLayer(CLUSTER_ASSETS) as DeckLayerLike
    const assetLayer = this.getLayer(ASSETS) as DeckLayerLike
    if (!clusterLayer && !assetLayer) {
      console.warn(
        'No assset layer available, neither cluster nor asset. Trying to access to soon? please review from where you are calling this method',
      )
      return []
    }

    if (this.isVisible(CLUSTER_ASSETS)) {
      return (clusterLayer.props.assetsToShow ||
        clusterLayer.props.data) as AssetDataGeoJsonLayer[]
    }

    if (this.isVisible(ASSETS)) {
      return (assetLayer.props.assetsToShow ||
        clusterLayer.props.data) as AssetDataGeoJsonLayer[]
    }
    console.warn('No layer visible for assets, neither cluster nor asset')
    return []
  },
  getAssetsPaintendOnMap() {
    const clusterLayer = this.getLayer(CLUSTER_ASSETS) as DeckLayerLike
    const assetLayer = this.getLayer(ASSETS) as DeckLayerLike
    if (!clusterLayer && !assetLayer) {
      console.error(
        'No assset layer available, neither cluster nor asset when calling getAssetsPaintendOnMap',
      )
      return []
    }

    if (this.isVisible(CLUSTER_ASSETS)) {
      return (clusterLayer.props.assetsToShow ||
        clusterLayer.props.data) as AssetDataGeoJsonLayer[]
    }

    if (this.isVisible(ASSETS)) {
      return ((assetLayer.state as Record<string, unknown>).data ||
        assetLayer.props.assetsToShow ||
        clusterLayer.props.data) as AssetDataGeoJsonLayer[]
    }
    console.error(
      'No layer visible for assets, neither cluster nor asset when calling getAssetsPaintendOnMap',
    )
    return []
  },
  getDataFromAssetLayer() {
    let layer = this.getLayer(ASSETS)
    let layerName = ASSETS
    if (this.isVisible(CLUSTER_ASSETS)) {
      layer = this.getLayer(CLUSTER_ASSETS)
      layerName = CLUSTER_ASSETS
    }
    if (!layer) {
      // If deck does not has the layer prepared, we got directly for the data
      // eslint-disable-next-line no-console
      console.debug(
        `🟡 deck.gl does not have the layer ${layerName} prepared, getting data from backup layers`,
      )
      const layer = this.layers[layerName]
      if (!layer) {
        return []
      } else {
        return layer.data as AssetDataGeoJsonLayer[]
      }
    }
    // When a layer is not found, layer is undefined — guard before accessing props
    if (!layer) {
      console.warn('No layer available for getting data, returning empty array')
      return []
    }
    if (isArrayEmpty(layer as unknown as unknown[])) {
      console.warn('No layer available for getting data, returning empty array')
      return []
    }
    return (layer as DeckLayerLike).props.data as AssetDataGeoJsonLayer[]
  },
  getDataFromExcludedAssetLayer() {
    const layer = this.getLayer(EXCLUDED_ASSETS) as DeckLayerLike
    if (layer && this.isVisible(EXCLUDED_ASSETS)) {
      return layer.props.assetsExcluded as AssetDataGeoJsonLayer[]
    }
    return []
  },
  async waitForLayerManagerToBeReady() {
    const maxRetries = 10
    const msToWaitBetweenRetries = 1000
    let retries = 0
    let layerManager = null
    while (!layerManager && retries < maxRetries) {
      try {
        // `layerManager` is a protected deck.gl internal accessed defensively here.
        layerManager = (
          this.deckInstance as unknown as { layerManager: unknown }
        ).layerManager
      } catch (e) {
        console.warn(
          `Deck.gl Layer manager not ready when trying to access. Retrying in ${msToWaitBetweenRetries}ms`,
        )
        await waitMS(msToWaitBetweenRetries)
        retries++
      }
    }
    if (retries === maxRetries) {
      throw new Error('Deck.gl Layer manager not ready when trying to access')
    }
  },
  flyTo(boundingBox, comesFromGeocoder = false) {
    if (!this.deckInstance) {
      console.warn('Deck instance is not initialized, skipping flyTo')
      return
    }
    console.debug(
      '🔰 LayerService: flying to | XMIN:',
      boundingBox[0][0],
      'YMIN:',
      boundingBox[0][1],
      'XMAX:',
      boundingBox[1][0],
      'YMAX:',
      boundingBox[1][1],
    )
    const TRANSITION_DURATION = 750
    // `layerManager` is a protected deck.gl internal; `fitBounds` lives on the
    // active (WebMercator) viewport, not the base `Viewport` type.
    const viewport = (
      this.deckInstance as unknown as {
        layerManager: { context: { viewport: unknown } }
      }
    ).layerManager.context.viewport as {
      fitBounds: (
        b: BoundingBox,
        opts?: { padding?: number; maxZoom?: number },
      ) => {
        latitude: number
        longitude: number
        zoom: number
      }
    }
    // deck.gl's fitBounds expects [[minLng, minLat], [maxLng, maxLat]] (SW, NE);
    // a box with west > east fits ~340deg of longitude (whole world) in deck 9.
    // Callers pass the two corners in inconsistent orders — getBoundingBoxFromAssetsGeometry
    // yields [[maxLng, minLat], [minLng, maxLat]] (flyToAssets), while $flyToFrame /
    // geocoder pass [[minLng, minLat], [maxLng, maxLat]]. Normalize to SW/NE via
    // min/max so every caller fits correctly regardless of corner order.
    const deckBounds: BoundingBox = [
      [
        Math.min(boundingBox[0][0], boundingBox[1][0]),
        Math.min(boundingBox[0][1], boundingBox[1][1]),
      ],
      [
        Math.max(boundingBox[0][0], boundingBox[1][0]),
        Math.max(boundingBox[0][1], boundingBox[1][1]),
      ],
    ]
    // Cap the fit zoom only when fitting a selection of assets (flyToAssets,
    // comesFromGeocoder=false), where over-zooming breaks the asset-id label
    // de-clutter grid (deck 9 §7.5). Flying to a single asset (list
    // double-click) or a geocoder result passes comesFromGeocoder=true and
    // should zoom in naturally to the target (matches pre-deck-9 behavior).
    const fitOptions = comesFromGeocoder
      ? { padding: FLY_TO_PADDING_PX }
      : { padding: FLY_TO_PADDING_PX, maxZoom: FLY_TO_MAX_ZOOM }
    const { latitude, longitude, zoom } = viewport.fitBounds(
      deckBounds,
      fitOptions,
    )
    const viewState = {
      zoom: Math.floor(zoom),
      longitude,
      latitude,
      bearing: 0,
      pitch: 0,
      transitionDuration: TRANSITION_DURATION,
      transitionInterpolator: new FlyToInterpolator(),
    }
    // Without this, a flyTo issued after the map has been idle (e.g. applying
    // a geoboundary filter) starts with the idle gap already elapsed and snaps.
    this.deckInstance.resetTransitionClock()
    this.deckInstance.setProps({ viewState })
    // Defer the resize: dispatching it synchronously right after starting the
    // transition cancels the transition in deck.gl 9.
    setTimeout(
      () => window.dispatchEvent(new Event('resize')),
      TRANSITION_DURATION,
    )
  },
  flyToGoogleMaps(boundingBox, comesFromGeocoder, caller) {
    console.debug(
      `🔰  LayerService:  -> flyToGoogleMaps, from ${caller}`,
      comesFromGeocoder,
    )
    const baseMap =
      this.getBaseMap() as unknown as GoogleMapsOverlayWithMap | null
    if (!baseMap || !baseMap._map) {
      throw Error('BaseMap is not initialized')
    }

    let boundigBoxGMaps = {
      east: boundingBox[0][0],
      south: boundingBox[0][1],
      north: boundingBox[1][1],
      west: boundingBox[1][0],
    }

    if (comesFromGeocoder) {
      boundigBoxGMaps = {
        east: boundingBox[1][0],
        south: boundingBox[1][1],
        north: boundingBox[0][1],
        west: boundingBox[0][0],
      }
    }

    baseMap._map.fitBounds(boundigBoxGMaps)
  },
  flyToBbox(boundingBox, comesFromGeocoder, caller) {
    if (this.isAppBasemapGoogleMaps()) {
      this.flyToGoogleMaps(boundingBox, comesFromGeocoder, caller)
    } else {
      this.flyTo(boundingBox, comesFromGeocoder)
    }
  },
  flyToSwitchingBasemapGoogleMaps(center, zoom) {
    const baseMap =
      this.getBaseMap() as unknown as GoogleMapsOverlayWithMap | null
    if (!baseMap || !baseMap._map) {
      throw Error('BaseMap is not initialized')
    }
    // eslint-disable-next-line no-undef
    const centerFormatted = new google.maps.LatLng(center[1], center[0])
    baseMap._map.setCenter(centerFormatted)
    baseMap._map.setZoom(zoom)
  },
  flyToSwitchingBasemapCarto(center, zoom) {
    const viewState = {
      zoom,
      longitude: center[0],
      latitude: center[1],
      transitionDuration: 750,
      transitionInterpolator: new FlyToInterpolator(),
    }
    const deck = this.deckInstance as DeckMap
    deck.resetTransitionClock()
    deck.setProps({ viewState })
  },
  flyToSwitchingBasemap(center, zoom) {
    console.debug('🔰 LayerService: flyToSwitchingBasemap', { center, zoom })
    if (this.isAppBasemapGoogleMaps()) {
      this.flyToSwitchingBasemapGoogleMaps(center, zoom)
    } else {
      this.flyToSwitchingBasemapCarto(center, zoom)
    }
  },
}

export default layerService
